import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'react-toastify';

const EditUnitModal = ({ asset, unit, unitIndex, isOpen, onClose, onUnitUpdated }) => {
  const [unitData, setUnitData] = useState({
    image: unit?.image || '',
    specifications: unit?.specifications || {},
    status: unit?.status || 'available'
  });

  const [imagePreview, setImagePreview] = useState(unit?.image || '');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !unit) return null;

  // Get specifications from asset model
  const assetSpecifications = asset?.specifications || {};

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUnitData({ ...unitData, image: reader.result });
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setUnitData({ ...unitData, image: '' });
    setImagePreview('');
  };

  const handleSpecChange = (specName, value) => {
    setUnitData({
      ...unitData,
      specifications: {
        ...unitData.specifications,
        [specName]: value
      }
    });
  };

  const handleStatusChange = (e) => {
    setUnitData({ ...unitData, status: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${API_ENDPOINTS.ASSETS}/${asset._id}/units/${unitIndex}`,
        unitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Unit updated successfully!');
      onUnitUpdated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update unit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '20px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>📝 Edit Unit: {unit.unitId}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Status */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
            Status
          </label>
          <select
            value={unitData.status}
            onChange={handleStatusChange}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="available">Available</option>
            <option value="in_use">In Use</option>
            <option value="under_maintenance">Under Maintenance</option>
            <option value="disposed">Disposed</option>
          </select>
        </div>

        {/* Image Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
            📸 Unit Image
          </label>
          {imagePreview ? (
            <div style={{ marginBottom: '10px' }}>
              <img
                src={imagePreview}
                alt={unit.unitId}
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  marginBottom: '10px'
                }}
              />
              <div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    background: '#fed7d7',
                    color: '#c53030',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginRight: '5px'
                  }}
                >
                  Remove Image
                </button>
                <label
                  style={{
                    background: '#bee3f8',
                    color: '#2c5282',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'inline-block'
                  }}
                >
                  Change Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          ) : (
            <label
              style={{
                display: 'block',
                padding: '30px',
                border: '2px dashed #cbd5e0',
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: '#f7fafc'
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>Click to upload image</div>
            </label>
          )}
        </div>

        {/* Specifications */}
        {Object.keys(assetSpecifications).length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#2d3748' }}>
              📋 Specifications
            </label>
            <div style={{ display: 'grid', gap: '10px' }}>
              {Object.keys(assetSpecifications).map((specKey) => (
                <div key={specKey}>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#4a5568', fontWeight: '500' }}>
                    {specKey}
                  </label>
                  <input
                    type="text"
                    value={unitData.specifications[specKey] || ''}
                    onChange={(e) => handleSpecChange(specKey, e.target.value)}
                    placeholder={`Enter ${specKey}`}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '25px' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              background: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              background: loading ? '#cbd5e0' : '#0f4c81',
              color: '#ffffff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUnitModal;
